<template>
  <ValidationObserver
    ref="observer"
    v-slot="{ invalid }"
    tag="div"
  >
    <NoEligibleUsers
      v-if="showNoEligibleUsersMessage"
      :action="action"
      data-test-id="SuspendUserModal__noEligibleUsers"
      :item="item"
      :showModal="showModal"
      :title="headerText"
      @close="handleClose"
    />
    <ModalConfirmation
      v-else
      :class="$style.modal"
      data-test-id="SuspendUserModal__confirmation"
      hasBlur
      :isDisabledPrimary="invalidDate || shouldDisablePrimary || invalid"
      :showModal="showModal"
      :title="headerText"
      @cancel="handleClose"
      @close="handleClose"
      @save="updateUser"
    >
      <template v-slot:body>
        <ScheduleNotification
          :inAsidePanel="inAsidePanel"
          :notificationAction="action"
        />
        <ParagraphBody
          v-if="!inAsidePanel"
        >
          <MarkdownInline
            data-test-id="SuspendUserModal__multipleUsersText"
            :mdTemplate="multipleUsersText"
          />
        </ParagraphBody>
        <ParagraphBody
          :data-test-id="$testId('description')"
        >
          Suspending an account will immediately remove a user's access to all JumpCloud provided
          resources. They will be retained in the system and billed as normal users.
        </ParagraphBody>
        <SegmentedButton
          :class="$style.suspendButtons"
          name="suspendButtons"
          :options="$options.suspensionOptions"
          :value="suspensionChoice"
          @input="updateSuspensionChoice"
        />
        <div
          :class="$style.fieldContainer"
          :data-test-id="$testId('dateTimePicker')"
        >
          <EventDateTime
            v-if="showDate"
            v-model="eventDate"
            :action="$options.suspend"
            :data-test-id="$testId('eventDateTime')"
            @dateIsInvalid="handleDateIsInvalid"
          />
          <ParagraphBody
            v-if="!showDate"
            :data-test-id="$testId('suspensionInformation')"
          >
            While Suspended, the user will have no access to any resources that they are bound to.
            You may schedule activation at any point in the future from the User profile or from the
            Users list.
            <Link
              data-test-id="SuspendMondal__learnMore"
              :href="$options.kbLink"
              isExternal
              target="_blank"
              text="Learn more about activation and user states"
            />
          </ParagraphBody>
          <ParagraphBody
            v-if="sambaUserText"
          >
            <MarkdownInline
              data-test-id="SuspendUserModal__sambaUserWarning"
              :mdTemplate="sambaUserText"
            />
          </ParagraphBody>
        </div>
      </template>
    </ModalConfirmation>
  </ValidationObserver>
</template>

<script>
import {
  Link,
  MarkdownInline,
  ModalConfirmation,
  ParagraphBody,
  SegmentedButton,
} from '@jumpcloud/ui-components';
import { ValidationObserver } from 'vee-validate3';
import { clone, isEmpty } from 'lodash';
import { mapActions, mapGetters } from 'vuex';
import { notificationModuleName as notificationModule } from '@jumpcloud/ui-notifications';
import { userStateActionType } from 'client/systemusers/constants/SystemUserConstants';
import Constants, { ModuleConsts } from 'client/util/Constants';
import EligibleUserMixin from 'client/systemusers/mixins/EligibleUserMixin';
import EventDateTime from 'client/systemusers/components/EventDateTime.vue';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';
import ModalHelper from 'client/systemusers/Modals/ModalHelper';
import NoEligibleUsers from 'client/systemusers/Modals/NoEligibleUsers.vue';
import ScheduleNotification from 'client/systemusers/components/ScheduleNotification.vue';
import pluralize from 'pluralize';

const { suspend } = userStateActionType;
const { suspended } = ModuleConsts.systemUser.accountStates;

const {
  suspensionOptionTypes,
  generateSuspensionNotification,
  setDefaultActionDate,
} = ModalHelper;

const { error, success } = Constants;
const systemUserCollection = 'SystemUserCollection';

export default {
  name: 'SuspendUserModal',

  components: {
    Link,
    EventDateTime,
    ParagraphBody,
    MarkdownInline,
    ModalConfirmation,
    NoEligibleUsers,
    ScheduleNotification,
    SegmentedButton,
    ValidationObserver,
  },

  mixins: [EligibleUserMixin],

  props: {
    item: {
      type: Object,
      default: undefined,
    },

    showModal: Boolean,
  },

  data() {
    // Need to define `action` for use in the mixin; it was previously
    // a prop that was passed in
    return {
      action: suspend,
      emailSettings: {
        sendWelcomeEmail: false,
        welcomeEmailAddress: '',
      },
      suspensionChoice: this.$options.schedule,
      eventDate: setDefaultActionDate(userStateActionType.suspend),
      isDateInvalid: false,
      shouldDisablePrimary: false,
    };
  },

  computed: {
    ...mapGetters('SystemUserModel', {
      userDisplayName: 'displayName',
    }),

    ...mapGetters(systemUserCollection, {
      selectedItems: 'getSelectedItems',
      usersIneligibleForSuspension: 'getUsersIneligibleForSuspension',
    }),

    confirmationText() {
      const selectedCount = this.selectedItems.length;
      const userCount = !this.item ? selectedCount - this.ineligibleCount : 1;
      const pronoun = pluralize('this', userCount);
      const target = pluralize('user', userCount);

      return `Are you sure you want to suspend ${pronoun} ${target}?`;
    },

    eligibleUsers() {
      if (this.item) {
        return this.item.state === suspended;
      }
      return (this.selectedItems.length - this.ineligibleCount) <= 0;
    },

    headerText() {
      const userText = this.inAsidePanel ? 'User' : 'Users';
      return `Suspend ${userText}`;
    },

    // If we are on the aside, `item` will be defined
    inAsidePanel() {
      return !isEmpty(this.item);
    },

    invalidDate() {
      return this.showDate && this.isDateInvalid;
    },

    multipleUsersText() {
      const selectedCount = this.selectedItems.length;
      const userCount = selectedCount - this.ineligibleCount;
      const userScheduleCount = selectedCount - this.sambaUserCount;

      const resultText = (this.suspensionChoice === this.$options.schedule)
        ? `**${userScheduleCount} of ${selectedCount}** selected users have accounts eligible for suspension.`
        : `**${userCount} of ${selectedCount}** selected users have accounts eligible for suspension.`;
      return resultText;
    },

    sambaUserCount() {
      const {
        sambaServiceUsers,
      } = this.usersIneligibleForSuspension;

      return sambaServiceUsers.length;
    },

    showDate() {
      return this.suspensionChoice === this.$options.schedule;
    },

    showNoEligibleUsersMessage() {
      return this.inAsidePanel
        ? this.isSambaUser
        : this.selectedItems.length === this.sambaUserCount;
    },

  },
  accountStates: ModuleConsts.systemUser.accountStates,

  suspend: userStateActionType.suspend,

  suspensionOptions: [
    // These need to be in this order for the button group
    suspensionOptionTypes.schedule,
    suspensionOptionTypes.now,
  ],

  kbLink: KbArticleUrls.managingUserStates,

  now: suspensionOptionTypes.now.value,

  schedule: suspensionOptionTypes.schedule.value,

  methods: {
    ...mapActions(notificationModule, [
      'triggerErrorNotification',
      'triggerSuccessNotification',
    ]),

    ...mapActions('SystemUserCollection', [
      'scheduleUserStateChange',
      'updateItems',
      'updateModifiedItemCount',
      'updateNextScheduledEvent',
      'unselectItems',
    ]),

    ...mapActions('SystemUserModel', [
      'suspendUser',
    ]),

    handleClose() {
      this.$emit('close');
    },

    handleDateIsInvalid(value) {
      this.isDateInvalid = value;
    },

    handleSave() {
      this.$emit('save');
    },

    /**
     * Display notification to admin depending on action
     */
    notifyAdmin(type, option, numSuccess) {
      const count = this.selectedItems.length;
      const usernameOrCount = count > 0 ? `${numSuccess} of ${count} users` : this.userDisplayName;

      const { message, title } = generateSuspensionNotification(type, option, usernameOrCount);

      if (type === success) {
        this.triggerSuccessNotification({ title, message });
      } else {
        this.triggerErrorNotification({ title, message });
      }
    },

    async scheduleSuspension() {
      const results = await this.scheduleUserStateChange({
        payload: {
          action: this.$options.suspend,
          emailSettings: this.emailSettings,
          eventDate: this.eventDate,
          ids: this.inAsidePanel ? [this.item._id] : this.selectedItems,
        },
      });

      const { failedJobs, scheduledUsers } = results;

      if (scheduledUsers.length) {
        // Update scheduled events list so that event badge displays
        const users = this.inAsidePanel ? [this.item._id] : scheduledUsers;
        // Schedule suspension for single user on aside view else multiple users on bulk view
        this.updateNextScheduledEvent(users);
        this.notifyAdmin(success, this.$options.schedule, scheduledUsers.length);
        this.unselectItems({ ids: this.selectedItems });

        // Let any components watching know that this user has been updated
        this.updateModifiedItemCount();
      } else if (failedJobs.length) {
        this.notifyAdmin(error, this.$options.schedule);
      }
    },

    async suspendNow() {
      // Suspend multiple users with bulk `updateItems` call
      try {
        if (this.selectedItems.length > 0 && !this.inAsidePanel) {
          const currentSelectedItems = clone(this.selectedItems);
          this.updateItems({
            ids: currentSelectedItems,
            attributes: {
              state: ModuleConsts.systemUser.accountStates.suspended,
              suspended: true, // Will be deprecated in the future
            },
          }).then((response) => {
            this.notifyAdmin(
              response.numSuccess ? success : error,
              this.$options.now,
              response.numSuccess,
            );
            this.unselectItems({ ids: this.selectedItems });
            // Let any components watching know that this user has been updated
            if (response.numSuccess) this.updateModifiedItemCount();
          });
        } else {
          // Suspend single user with `suspendUser` when on aside
          await this.suspendUser();

          this.notifyAdmin(success, this.$options.now);
          // Let any components watching know that this user has been updated
          this.updateModifiedItemCount();
        }
      } catch {
        this.notifyAdmin(error, this.$options.now);
      }
    },

    updateSuspensionChoice(value) {
      this.suspensionChoice = value;
      this.shouldDisablePrimary = value === this.$options.now
        ? this.eligibleUsers
        : false;
    },

    /**
     * Schedule Suspension: Create a scheduled event for the user(s)
     * Suspend now: Immediately suspend the user(s)
     */
    async updateUser() {
      const {
        sambaServiceUsers,
        suspendedUsers,
      } = this.usersIneligibleForSuspension || {};

      let ineligibleUsers = sambaServiceUsers;

      if (this.suspensionChoice === this.$options.schedule) {
        this.unselectItems({ ids: ineligibleUsers.map(item => item.id) });
        this.scheduleSuspension();
      } else if (this.suspensionChoice === this.$options.now) {
        ineligibleUsers = sambaServiceUsers.concat(suspendedUsers);

        this.unselectItems({ ids: ineligibleUsers.map(item => item.id) });
        this.suspendNow();
      }

      this.handleClose();
    },
  },

};
</script>

<style module scoped>
.fieldContainer {
  display: grid;
  min-height: var(--jc-size-12);
  overflow-y: auto;
}

.modal.modal {
  --modal-width: 67ch;
  --modal-max-width: 67ch;
}

/* Reach in and set the max-width so that the button text doesn't wrap */
.suspensionButtons /deep/ div {
  max-width: 50ch;
}

</style>
